import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from 'src/components/Card';
import { Label } from 'src/components/Label';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import type { WorkflowContactWindow } from 'src/interfaces/workflows.interface';
import { useWizard } from 'src/pages/Automations/components/wizard/WizardContext';
import { CampaignOverviewTab } from '../types';
import { ScheduleSelector } from '../../components/ScheduleSelector';

type ScheduleSettingsProps = {
  className?: string;
};

export const ScheduleSettings = ({ className = '' }: ScheduleSettingsProps) => {
  const { id: campaignReferenceId } = useParams();
  const navigate = useNavigate();
  const { get } = useAuthedRequest();
  const {
    campaignAutomation,
    setCampaignAutomation,
    contactWindow,
    isInitializing,
  } = useWizard();

  const [contactWindows, setContactWindows] = useState<WorkflowContactWindow[]>(
    [],
  );
  const [isLoadingContactWindows, setIsLoadingContactWindows] = useState(false);

  // Fetch contact windows list
  useEffect(() => {
    if (isInitializing) return;

    (async () => {
      setIsLoadingContactWindows(true);
      try {
        const fetchedContactWindows = (await get(
          '/workflow_contact_windows',
        )) as WorkflowContactWindow[];
        const records = Array.isArray(fetchedContactWindows)
          ? fetchedContactWindows
          : [];
        setContactWindows(records);
      } catch {
        setContactWindows([]);
      } finally {
        setIsLoadingContactWindows(false);
      }
    })();
  }, [get, isInitializing]);

  const handleNavigateToScheduleEditor = useCallback(
    (contactWindowId?: string) => {
      const returnTo = `/beta/campaigns/overview/${campaignReferenceId}?tab=${CampaignOverviewTab.SETTINGS}`;
      const params = new URLSearchParams({ returnTo });
      if (contactWindowId) {
        params.set('id', contactWindowId);
      }
      navigate(`/beta/campaigns/contact-window?${params.toString()}`);
    },
    [campaignReferenceId, navigate],
  );

  const handleContactWindowChange = useCallback(
    (cw: WorkflowContactWindow | null) => {
      if (cw) {
        // When selecting a contact window, clear legacy schedule
        setCampaignAutomation({
          ...campaignAutomation,
          contactWindowId: cw.id,
          schedule: undefined,
        });
      } else {
        setCampaignAutomation({
          ...campaignAutomation,
          contactWindowId: undefined,
        });
      }
    },
    [campaignAutomation, setCampaignAutomation],
  );

  // Determine if this is a legacy campaign
  const isLegacyCampaign =
    !campaignAutomation.contactWindowId && !!campaignAutomation.schedule;

  return (
    <div className={`space-y-8 ${className}`}>
      <div>
        <h2 className="text-2xl font-bold mb-2">Schedule</h2>
        <p className="text-neutral-600 mb-6">
          Configure when this campaign will send messages or place calls to
          contacts.
        </p>

        <Card className="p-6 bg-neutral-50 border-neutral-100">
          <Label>Campaign schedule</Label>
          <ScheduleSelector
            contactWindows={contactWindows}
            selectedContactWindow={contactWindow}
            onContactWindowChange={handleContactWindowChange}
            isLoading={isLoadingContactWindows || isInitializing}
            legacySchedule={
              isLegacyCampaign ? campaignAutomation.schedule : undefined
            }
            onNavigateToScheduleEditor={handleNavigateToScheduleEditor}
          />
        </Card>
      </div>
    </div>
  );
};
